import React, { useState, useEffect } from 'react';
import { FaCoins, FaAd, FaClock, FaStopwatch, FaQuestion, FaSave, FaTimesCircle } from 'react-icons/fa';
import Calling from '../../api/Calling';
import Navbar from './Navbar';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';

const QuizWrong = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({
        coin: 1,
        ad_position: 1,
        timing: 1,
        timeout_timing: 1,
        question: 1
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const resp = await Calling.getQuizWrong();
            if (resp?.status) {
                setData(resp.data);
                setError(null);
            } else {
                setError(resp?.message || 'Failed to fetch data');
                toast.error(resp?.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error(error);
            setError('Failed to fetch data. Please try again.');
            toast.error('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Convert string values to numbers and ensure minimum of 1
        const numValue = parseInt(value, 10);
        const validValue = isNaN(numValue) ? 1 : Math.max(1, numValue);

        setData({
            ...data,
            [name]: validValue
        });
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        // Validation
        const invalidFields = Object.entries(data).filter(([key, value]) => value < 1);
        if (invalidFields.length > 0) {
            const fieldNames = invalidFields.map(([key]) => key).join(', ');
            setError(`All values must be at least 1. Please check: ${fieldNames}`);
            toast.error(`All values must be at least 1. Please check: ${fieldNames}`);
            setSaving(false);
            return;
        }

        try {
            const body = {
                coin: data.coin,
                ad_position: data.ad_position,
                timing: data.timing,
                timeout_timing: data.timeout_timing,
                question: data.question
            }
            const resp = await Calling.updateQuizWrong(body);
            if (resp?.status) {
                setSuccess(resp?.message || 'Settings updated successfully!');
                toast.success(resp?.message || 'Settings updated successfully!');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(resp?.message || 'Failed to update settings');
                toast.error(resp?.message || 'Failed to update settings');
            }
        } catch (error) {
            console.error(error);
            setError('Failed to update settings. Please try again.');
            toast.error('Failed to update settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader loading={loading} />
            </div>
        );
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'wrong'} />
                <hr className='border-main-border' />

                <div className="rounded-xl p-5">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 text-main-delete rounded-md border border-red-200">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
                            {success}
                        </div>
                    )}

                    <div className="bg-main-bg rounded-xl p-5 border border-main-border">
                        <h3 className="text-lg font-semibold mb-4 text-main-font">
                            <div className="flex items-center">
                                <FaTimesCircle className="mr-2 text-main-delete" />
                                Wrong Answer Quiz Settings
                            </div>
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-main-font mb-2" htmlFor="coin">
                                    <div className="flex items-center">
                                        <FaCoins className="mr-2 text-main-blue" /> Coins
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="coin"
                                    name="coin"
                                    value={data.coin}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Coins awarded per question</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="ad_position">
                                    <div className="flex items-center">
                                        <FaAd className="mr-2 text-main-blue" /> Ad Position
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="ad_position"
                                    name="ad_position"
                                    value={data.ad_position}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Position to show (Fullscreen / Open) ads</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="timing">
                                    <div className="flex items-center">
                                        <FaClock className="mr-2 text-main-blue" /> Timing (seconds)
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="timing"
                                    name="timing"
                                    value={data.timing}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Time per question</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="timeout_timing">
                                    <div className="flex items-center">
                                        <FaStopwatch className="mr-2 text-main-blue" /> Timeout Timing (seconds)
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="timeout_timing"
                                    name="timeout_timing"
                                    value={data.timeout_timing}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Timeout between questions</p>
                            </div>

                            <div>
                                <label className="block text-main-font mb-2" htmlFor="question">
                                    <div className="flex items-center">
                                        <FaQuestion className="mr-2 text-main-blue" /> Questions
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    id="question"
                                    name="question"
                                    value={data.question}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                    min="1"
                                    onWheel={(e) => e.target.blur()}
                                />
                                <p className="text-sm text-main-subfont mt-1">Number of questions per quiz</p>
                            </div>
                        </div>

                        <div className="flex justify-end mt-6">
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className={`px-6 py-2.5 rounded-lg text-white ${saving ? 'bg-opacity-70' : 'hover:bg-[#5A3DEE] transform hover:scale-[1.02]'
                                    } bg-main-blue transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-main-blue flex items-center`}
                            >
                                {saving ? (
                                    <>
                                        <span className="mr-2 inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="mr-2" />
                                        Save Settings
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuizWrong;